/**
 * Web Vitals tracking for React Compiler migration baseline and comparison
 *
 * Captures Core Web Vitals (CLS, FCP, INP, LCP, TTFB) and sends them to Amplitude
 * for performance monitoring and A/B comparison between compiler-enabled and
 * non-compiler builds.
 *
 * @see https://web.dev/vitals/
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import type { Metric } from 'web-vitals';
import { amplitude } from '@lib/amplitude';
import { logger } from '@utils/logger';

// Feature flag for React Compiler - will be set to 'true' once compiler is enabled
const isReactCompilerBuild = import.meta.env.VITE_REACT_COMPILER === 'true';

/**
 * Send a web vital metric to Amplitude.
 *
 * No retry loop: the Amplitude SDK buffers events fired before init in its own
 * dispatch queue, so we call `amplitude.track` directly and let the SDK flush once
 * ready. We only skip (logging at `info`, never `error`) when Amplitude will NOT
 * initialize at all — local `vite dev`, a missing API key, or the dev environment
 * without the `VITE_ENABLE_AMPLITUDE` override — mirroring `initializeAmplitude`'s
 * own gate so deploys that intentionally run without Amplitude don't spam errors.
 */
function sendToAmplitude(metric: Metric): void {
  const amplitudeWillInit =
    // AIDEV-NOTE: Use import.meta.env.DEV for Vite compatibility (not process.env.NODE_ENV)
    !import.meta.env.DEV &&
    Boolean(import.meta.env.VITE_AMPLITUDE_API_KEY) &&
    (import.meta.env.VITE_APP_ENV !== 'dev' || import.meta.env.VITE_ENABLE_AMPLITUDE === 'true');

  if (!amplitudeWillInit) {
    logger.info(
      `[WebVitals] ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating}); Amplitude disabled, not tracking`,
    );
    return;
  }

  try {
    amplitude.track('web_vitals', {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      navigation_type: metric.navigationType,
      // React Compiler tracking
      react_compiler: isReactCompilerBuild,
      // Build identification
      build_timestamp: (import.meta.env.VITE_BUILD_TIMESTAMP as string | undefined) ?? null,
    });
  } catch (error) {
    logger.warn('Failed to send web vital to Amplitude:', error);
  }
}

/**
 * Initialize Web Vitals tracking
 *
 * Call this once at app startup (in index.tsx) to begin collecting metrics.
 * Metrics are automatically sent to Amplitude when they become available.
 */
export function initWebVitals(): void {
  try {
    // Cumulative Layout Shift - measures visual stability
    onCLS(sendToAmplitude);

    // First Contentful Paint - measures perceived load speed
    onFCP(sendToAmplitude);

    // Interaction to Next Paint - measures responsiveness (replaces FID)
    onINP(sendToAmplitude);

    // Largest Contentful Paint - measures loading performance
    onLCP(sendToAmplitude);

    // Time to First Byte - measures server response time
    onTTFB(sendToAmplitude);

    logger.info('[WebVitals] Initialized web vitals tracking');
  } catch (error) {
    logger.error('[WebVitals] Failed to initialize:', error);
  }
}

// Export the feature flag for use in other modules
export { isReactCompilerBuild };
